import React, { useState, useEffect } from "react";
import { NAV_ITEMS } from "./constants/adminConstants";
import AdminProfilePanel from "./AdminProfilePanel";
import "./css/adminStyles.css";

function AdminLayout({ user, activeSection, onNavigate, onLogout, children }) {
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [profileOpen, setProfileOpen] = useState(false);
  const [expandedParents, setExpandedParents] = useState(() => {
    if (!user) return new Set();
    const items = NAV_ITEMS[user.role] || [];
    const expanded = new Set();
    const collectExpanded = (list) => {
      list.forEach((item) => {
        if (item.children) {
          if (item.children.some((c) => c.path === activeSection || (c.children && c.children.some((gc) => gc.path === activeSection)))) {
            expanded.add(item.path);
          }
          collectExpanded(item.children);
        }
      });
    };
    collectExpanded(items);
    return expanded;
  });

  const navItems = (NAV_ITEMS[user?.role] || []).filter(
    (item) => item.label !== "Notifications"
  );

  useEffect(() => {
    const items = NAV_ITEMS[user?.role] || [];
    const collectExpanded = (list) => {
      list.forEach((item) => {
        if (item.children) {
          if (item.children.some((c) => c.path === activeSection || (c.children && c.children.some((gc) => gc.path === activeSection)))) {
            setExpandedParents((prev) => {
              if (!prev.has(item.path)) {
                const next = new Set(prev);
                next.add(item.path);
                return next;
              }
              return prev;
            });
          }
          collectExpanded(item.children);
        }
      });
    };
    collectExpanded(items);
  }, [activeSection, user?.role]);

  const toggleParent = (path) => {
    setExpandedParents((prev) => {
      const next = new Set(prev);
      if (next.has(path)) {
        next.delete(path);
      } else {
        next.add(path);
      }
      return next;
    });
  };

  const getActiveLabel = () => {
    if (profileOpen) return "Profile";
    const findLabel = (list) => {
      for (const item of list) {
        if (item.path === activeSection) return item.label;
        if (item.children) {
          const found = findLabel(item.children);
          if (found) return found;
        }
      }
      return null;
    };
    return findLabel(navItems) || "Dashboard";
  };

  const isActiveOrChildActive = (item) => {
    if (item.path === activeSection) return true;
    if (item.children) {
      return item.children.some((c) => c.path === activeSection || (c.children && c.children.some((gc) => gc.path === activeSection)));
    }
    return false;
  };

  const renderNavItem = (item, depth = 0) => {
    if (item.children) {
      const hasActive = isActiveOrChildActive(item);
      return (
        <div key={item.path} className="AdminNavGroup" style={depth > 0 ? { marginLeft: 12 } : {}}>
          <div
            className={`AdminNavItem AdminNavParent ${!profileOpen && hasActive ? "AdminNavItemActive" : ""}`}
          >
            <button
              className="AdminNavParentBtn"
              onClick={() => { setProfileOpen(false); onNavigate(item.path); }}
            >
              {item.label}
            </button>
            <span
              className={`AdminNavExpandIcon ${expandedParents.has(item.path) ? "AdminNavExpandIconOpen" : ""}`}
              onClick={() => { setProfileOpen(false); toggleParent(item.path); }}
            >
              &#9654;
            </span>
          </div>
          <div className={`AdminNavChildren ${expandedParents.has(item.path) ? "AdminNavChildrenOpen" : ""}`}>
            {item.children.map((child) => renderNavItem(child, depth + 1))}
          </div>
        </div>
      );
    }
    return (
      <button
        key={item.path}
        className={`AdminNavItem ${depth > 0 ? "AdminNavChild" : ""} ${!profileOpen && activeSection === item.path ? "AdminNavItemActive" : ""}`}
        style={depth > 0 ? { paddingLeft: 24 + (depth - 1) * 12 } : {}}
        onClick={() => { setProfileOpen(false); onNavigate(item.path); }}
      >
        {item.label}
      </button>
    );
  };

  return (
    <div className={`AdminLayout ${sidebarOpen ? "" : "AdminLayout--sidebar-collapsed"}`}>
      <div className={`AdminSidebar ${sidebarOpen ? "" : "AdminSidebar--collapsed"}`}>
        <div className="AdminSidebarUser">
          <div className="AdminSidebarAvatar" title={`${user.firstName} ${user.lastName}`} onClick={() => setProfileOpen(true)}>{user.firstName?.[0]}{user.lastName?.[0]}</div>
          <span className="AdminSidebarUserId">{user.loginId}</span>
        </div>

        <nav>
          {navItems.map((item) => renderNavItem(item))}
        </nav>

        <button className="AdminSidebarLogout" onClick={onLogout}>
          Sign Out
        </button>
      </div>

      <div className="AdminMain">
        <div className="AdminHeader">
          <div className="AdminHeaderLeft">
            <button className="AdminSidebarToggle AdminSidebarToggleDesktop" onClick={() => setSidebarOpen(!sidebarOpen)}>
              {sidebarOpen ? "\u2715" : "\u2630"}
            </button>
            <h1 className="AdminHeaderTitle">
              {getActiveLabel()}
            </h1>
          </div>
        </div>

        {profileOpen ? (
          <AdminProfilePanel user={user} onClose={() => setProfileOpen(false)} />
        ) : (
          children
        )}
      </div>
    </div>
  );
}

export default AdminLayout;
